/*
 * Wake product section.
 *
 * Three small behaviours layered on top of Dawn's own components:
 *   1. Bundle tiers behave as a radio group driving price and cart quantity.
 *   2. Prev/next arrows over the gallery, driving Dawn's media-gallery API so
 *      the thumbnails stay in sync.
 *   3. The non-active gallery images are promoted from lazy to eager once the
 *      page has loaded, so switching image is instant rather than a blank wait.
 */

(function () {
  /* ---------------------------------------------------------------- tiers */

  function initTiers(root) {
    const tiers = Array.from(root.querySelectorAll('[data-wake-tier]'));
    if (!tiers.length) return;

    const quantityInput = root.querySelector('[data-wake-quantity]');
    const priceEls = Array.from(root.querySelectorAll('[data-wake-price]'));
    const compareEls = Array.from(root.querySelectorAll('[data-wake-compare]'));

    function select(tier) {
      const radio = tier.querySelector('.wake-tier__radio');
      if (!radio) return;

      radio.checked = true;
      tiers.forEach(function (other) {
        other.classList.toggle('is-selected', other === tier);
      });

      const qty = radio.dataset.wakeTierQty || '1';
      if (quantityInput) quantityInput.value = qty;
      /* The buy bar shows the same bundle, so pressing it can never add a
         different quantity than the one selected up the page. */
      root.querySelectorAll('[data-wake-buybar-qty]').forEach(function (el) {
        el.textContent = qty;
      });

      /* Prices are pre-formatted server-side by Liquid's money filter, so this
         is theme-authored markup rather than user input. */
      if (radio.dataset.wakeTierPrice) {
        priceEls.forEach(function (el) {
          el.innerHTML = radio.dataset.wakeTierPrice;
        });
      }

      /* The struck-through figure is the list price for that many units, so it
         has to move with the tier. Hidden when a tier has nothing to save. */
      const compare = radio.dataset.wakeTierCompare;
      compareEls.forEach(function (el) {
        el.innerHTML = compare || '';
        el.hidden = !compare;
      });
    }

    tiers.forEach(function (tier) {
      const radio = tier.querySelector('.wake-tier__radio');
      if (!radio) return;
      radio.addEventListener('change', function () {
        if (radio.checked) select(tier);
      });
    });

    const checked = tiers.find(function (tier) {
      const radio = tier.querySelector('.wake-tier__radio');
      return radio && radio.checked;
    });
    select(checked || tiers[0]);
  }

  /* -------------------------------------------------------------- gallery */

  function initGallery(root) {
    const wrapper = root.querySelector('.wake-gallery');
    const gallery = root.querySelector('media-gallery');
    if (!wrapper || !gallery) return;

    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    if (!viewer) return;

    /* Each slide also contains a zoom <button data-media-id>, so this must be
       scoped to the list items or the arrows land on a button and no slide
       ends up active. */
    const slides = Array.from(viewer.querySelectorAll('li.product__media-item[data-media-id]'));
    const prev = wrapper.querySelector('[data-wake-gallery-prev]');
    const next = wrapper.querySelector('[data-wake-gallery-next]');

    /* Moved inside the viewer, which wraps exactly the visible image, so the
       arrows centre on it and travel with it instead of being positioned
       against the wrapper's full height including the thumbnail strip. */
    if (prev) viewer.appendChild(prev);
    if (next) viewer.appendChild(next);

    if (slides.length > 1 && prev && next) {
      /* The position is tracked here rather than re-derived from the DOM on
         every click. Reading it back meant depending on exactly how and when
         Dawn's setActiveMedia moves the is-active class, which is fragile;
         holding the index makes stepping deterministic. */
      let index = slides.findIndex(function (slide) {
        return slide.classList.contains('is-active');
      });
      if (index < 0) index = 0;

      const syncArrows = function () {
        prev.disabled = index === 0;
        next.disabled = index === slides.length - 1;
      };

      /* Thumbnails and swipes move the gallery without going through step(),
         so the tracked index is re-read from the DOM when that happens. */
      const syncFromDom = function () {
        const found = slides.findIndex(function (slide) {
          return slide.classList.contains('is-active');
        });
        if (found > -1) index = found;
        syncArrows();
      };

      const step = function (delta) {
        const target = index + delta;
        if (target < 0 || target > slides.length - 1) return;
        index = target;
        gallery.setActiveMedia(slides[index].dataset.mediaId, false);
        syncArrows();
      };

      prev.addEventListener('click', function () {
        step(-1);
      });
      next.addEventListener('click', function () {
        step(1);
      });

      gallery.addEventListener('click', function (event) {
        if (event.target === prev || event.target === next) return;
        if (prev.contains(event.target) || next.contains(event.target)) return;
        syncFromDom();
      });
      viewer.addEventListener('slideChanged', syncFromDom);

      syncArrows();
    }

    /* Dawn lazy-loads every image but the first, so the first switch shows an
       empty frame while the image is still on the wire. Warming them once the
       page has loaded keeps the initial paint fast and makes every later
       switch instant.
       Copying srcset and sizes before src means the browser resolves the same
       candidate URL it would pick for the real element, so this is a cache
       warm rather than a second download. */
    const promote = function () {
      viewer.querySelectorAll('img').forEach(function (img) {
        if (img.loading === 'lazy') img.loading = 'eager';
        if (img.complete) return;

        const warm = new Image();
        if (img.sizes) warm.sizes = img.sizes;
        if (img.srcset) warm.srcset = img.srcset;
        if (img.src) warm.src = img.src;
      });
    };

    if (document.readyState === 'complete') {
      promote();
    } else {
      window.addEventListener('load', promote, { once: true });
    }
  }

  /* ----------------------------------------------------------- thumbnails */

  /* The desktop thumbnail strip scrolls. Two jobs: fade whichever edge has
     more behind it, and keep the active thumbnail in view when the arrows or
     the keyboard change the image rather than a click on the strip itself. */
  function initThumbs(root) {
    const list = root.querySelector('.thumbnail-list');
    if (!list || list.dataset.wakeBound) return;
    list.dataset.wakeBound = 'true';

    function updateFades() {
      const max = list.scrollWidth - list.clientWidth;
      /* A pixel of tolerance: sub-pixel layout means scrollLeft rarely lands
         exactly on the maximum, which would leave the end fade stuck on. */
      list.classList.toggle('is-scrolled-start', list.scrollLeft > 1);
      list.classList.toggle('is-scrolled-end', list.scrollLeft < max - 1);
    }

    list.addEventListener('scroll', updateFades, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(updateFades).observe(list);

    /* Watching the attribute rather than hooking the arrows keeps this working
       whatever changes the selection — arrows, keyboard, or Dawn itself. */
    if ('MutationObserver' in window) {
      new MutationObserver(function (records) {
        records.forEach(function (record) {
          const thumb = record.target;
          if (thumb.getAttribute('aria-current') !== 'true') return;
          const item = thumb.closest('.thumbnail-list__item') || thumb;
          if (item.scrollIntoView) {
            item.scrollIntoView({ inline: 'nearest', block: 'nearest' });
          }
        });
      }).observe(list, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
    }

    updateFades();
  }

  /* -------------------------------------------------------------- buy bar */

  /* Shown once the buy box has scrolled out of view, and only while scrolling
     up — someone heading down the page is still reading, and a bar sliding in
     under them is just in the way. */
  function initBuyBar(root) {
    const bar = root.querySelector('[data-wake-buybar]');
    const form = root.querySelector('[data-wake-form]');
    if (!bar || !form || bar.dataset.wakeBound) return;
    bar.dataset.wakeBound = 'true';

    let buyBoxVisible = true;
    let lastY = window.scrollY;
    let ticking = false;

    function apply(show) {
      bar.classList.toggle('is-visible', show);
    }

    if ('IntersectionObserver' in window) {
      new IntersectionObserver(
        function (entries) {
          buyBoxVisible = entries[0].isIntersecting;
          if (buyBoxVisible) apply(false);
        },
        { rootMargin: '0px 0px -20% 0px' }
      ).observe(form);
    }

    window.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(function () {
          ticking = false;
          const y = window.scrollY;
          const delta = y - lastY;
          if (Math.abs(delta) < 6) return;
          apply(!buyBoxVisible && delta < 0);
          lastY = y;
        });
      },
      { passive: true }
    );
  }

  /* ----------------------------------------------------------- accordions */

  /* <details> gives the right semantics but snaps open. Animating the panel
     height by hand keeps those semantics and works in every browser, unlike
     ::details-content which is not universally supported yet.

     Only one panel in a group stays open: opening a second closes the first,
     so a phone never ends up scrolling through several expanded answers. */
  function initAccordions(root) {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    root.querySelectorAll('.wake-accordions').forEach(function (group) {
      const items = Array.from(group.querySelectorAll('.wake-accordion'));

      items.forEach(function (details) {
        const summary = details.querySelector('.wake-accordion__summary');
        const panel = details.querySelector('.wake-accordion__panel');
        if (!summary || !panel || summary.dataset.wakeBound) return;
        summary.dataset.wakeBound = 'true';

        let animation = null;

        function run(closing) {
          if (animation) animation.cancel();
          if (!closing) details.open = true;

          const full = panel.scrollHeight;
          animation = panel.animate(
            { height: [(closing ? full : 0) + 'px', (closing ? 0 : full) + 'px'] },
            { duration: closing ? 240 : 280, easing: 'cubic-bezier(0.32, 0.72, 0, 1)' }
          );
          animation.onfinish = function () {
            if (closing) details.open = false;
            animation = null;
          };
          animation.oncancel = function () {
            animation = null;
          };
        }

        /* Exposed so a sibling opening can close this one. */
        details.wakeCollapse = function () {
          if (!details.open) return;
          if (reduced.matches) details.open = false;
          else run(true);
        };

        summary.addEventListener('click', function (event) {
          const opening = !details.open;

          if (opening) {
            items.forEach(function (other) {
              if (other !== details && other.wakeCollapse) other.wakeCollapse();
            });
          }

          if (reduced.matches) return;
          event.preventDefault();
          run(!opening);
        });
      });
    });
  }

  /* ------------------------------------------------------------- delivery */

  /* Rendered client-side rather than in Liquid so the dates stay correct
     however long Shopify caches the page, and so the weekend skip is real. */
  function addBusinessDays(from, days) {
    const date = new Date(from.getTime());
    let added = 0;
    while (added < days) {
      date.setDate(date.getDate() + 1);
      const weekday = date.getDay();
      if (weekday !== 0 && weekday !== 6) added += 1;
    }
    return date;
  }

  function initDelivery(root) {
    /* Every match, not just the first: the estimate appears in the buy box and
       again in the closing call to action. */
    root.querySelectorAll('[data-wake-delivery]').forEach(fillDelivery);
  }

  function fillDelivery(target) {
    const min = parseInt(target.dataset.minDays, 10);
    const max = parseInt(target.dataset.maxDays, 10);
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;

    const locale = document.documentElement.lang || 'da';
    const today = new Date();
    const earliest = addBusinessDays(today, Math.max(0, min));
    const latest = addBusinessDays(today, Math.max(min, max));

    let text;
    try {
      const format = new Intl.DateTimeFormat(locale, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
      /* Each date is formatted on its own rather than with formatRange, whose
         separator is a tight dash with no spaces around it. */
      text = format.format(earliest) + ' – ' + format.format(latest);
    } catch (error) {
      text = earliest.toLocaleDateString() + ' – ' + latest.toLocaleDateString();
    }

    target.textContent = text;
  }

  /* ----------------------------------------------------------------- init */

  function init(scope) {
    const root = scope || document;

    root.querySelectorAll('.wake-product').forEach(function (el) {
      initTiers(el);
      initGallery(el);
      initThumbs(el);
      initBuyBar(el);
    });

    /* Accordions and the delivery estimate also appear outside the product
       section — the FAQ and the closing CTA — so these run across the whole
       scope rather than per product. */
    initAccordions(root);
    initDelivery(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
